import { pick, uniq, flatten, compact, map } from 'lodash-es'
import { getCurrentLang } from '#user/lib/i18n'
import { getEntitiesByUris, getEntitiesImages, getEntityImage, getEntityImagePath, type SerializedEntity } from '../entities.ts'
import { getBestLangValue } from '../get_best_lang_value.ts'

export async function addWorksImagesAndAuthors (works: SerializedEntity[]) {
  await Promise.all([
    addWorksImages(works),
    addWorksAuthors(works),
  ])
  return works
}

export async function addEntitiesImages (works: SerializedEntity[]) {
  const remainingEntities = works.slice(0)
  async function nextBatch () {
    const batchEntities = remainingEntities.splice(0, 20)
    if (batchEntities.length === 0) return
    await addMissingImages(batchEntities)
    return nextBatch()
  }
  await nextBatch()
  return works
}

export const addWorksImages = addEntitiesImages

async function addMissingImages (entities: SerializedEntity[]) {
  const entitiesWithoutImages = entities.filter(entity => !entity.images)
  const uris = map(entitiesWithoutImages, 'uri')
  const imagesByUri = await getEntitiesImages(uris)
  entities.forEach(entity => {
    const entityImages = imagesByUri[entity.uri]
    setEntityImages(entity, entityImages)
  })
}

export async function addEntityImages (entity: SerializedEntity) {
  const { type, uri } = entity
  let entityImages
  if (type === 'work' || type === 'serie') {
    entityImages = await getEntityImage(uri)
  }
  setEntityImages(entity, entityImages)
}

function setEntityImages (entity: SerializedEntity, entityImages) {
  entity.images = []
  if (entityImages) {
    const bestLang = getBestLangValue(getCurrentLang(), entity.originalLang, entityImages)
    if ('lang' in bestLang) {
      const { lang } = bestLang
      const preferredLangImages = entityImages[lang]
      if (preferredLangImages) {
        entity.images = preferredLangImages.map(getEntityImagePath)
      }
      if (entity.images[0]) {
        // @ts-expect-error
        entity.image = { url: entity.images[0] }
      }
    }
  } else if (entity.image?.url) {
    entity.images = [ entity.image.url ]
  }
}

export async function addWorksAuthors (works: SerializedEntity[]) {
  const authorsUris = uniq(compact(flatten(works.map(getWorkAuthorsUris))))
  const entities = await getEntitiesByUris({ uris: authorsUris })
  works.forEach(work => {
    const workAuthorUris = getWorkAuthorsUris(work)
    work.authors = Object.values(pick(entities, workAuthorUris))
  })
}

const getWorkAuthorsUris = (work: SerializedEntity) => work.claims['wdt:P50']
